import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, List, LogOut, Plus, Users, BarChart2,
  TrendingUp, Download, Building2, Filter, X, FileText,
  CheckSquare, Square, ChevronDown, RefreshCw, ArrowLeft
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, Legend
} from 'recharts';
import { publicaciones } from '../data/mockPublicaciones';
import '../demo-professional.css';

// ─── Shared Nav ───────────────────────────────────────────────────────────────
function DemoNav({ active }) {
  const navigate = useNavigate();
  const handleLogout = () => {
    localStorage.removeItem('demo_authenticated');
    navigate('/propuesta/demo');
  };
  const tabs = [
    { id: 'dashboard',     label: 'Dashboard',    icon: <LayoutDashboard size={15} />, path: '/propuesta/demo/dashboard' },
    { id: 'publicaciones', label: 'Publicaciones', icon: <List size={15} />,           path: '/propuesta/demo/publicaciones' },
    { id: 'revendedores',  label: 'Revendedores',  icon: <Users size={15} />,          path: '/propuesta/demo/revendedores' },
    { id: 'reportes',      label: 'Reportes',      icon: <BarChart2 size={15} />,      path: '/propuesta/demo/reportes' },
  ];
  return (
    <>
      <header className="demo-header sticky top-0 z-20">
        <div className="px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}>
              <Building2 className="text-white" size={18} />
            </div>
            <div>
              <h1 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Sistema de Tasación</h1>
              <p className="text-xs" style={{ color: '#94a3b8' }}>Vladimir & Aerocar</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate('/propuesta')} className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition hover:bg-white/5" style={{ color: '#94a3b8' }} data-testid="demo-back-to-propuesta" title="Volver a la propuesta">
              <ArrowLeft size={15} /><span className="hidden sm:inline">Propuesta</span>
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg" style={{ color: '#94a3b8' }}>
              <LogOut size={15} /><span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>
      <nav style={{ background: 'white', borderBottom: '1px solid #e2e8f0' }}>
        <div className="px-4 sm:px-6 py-2 flex items-center gap-1 sm:gap-2 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => navigate(tab.path)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition
                ${active === tab.id ? 'text-orange-600 bg-orange-50' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}>
              {tab.icon}<span>{tab.label}</span>
            </button>
          ))}
          <button onClick={() => navigate('/propuesta/demo/nueva')}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg ml-auto whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}>
            <Plus size={15} /><span>Nueva</span>
          </button>
        </div>
      </nav>
    </>
  );
}

// ─── Tooltip personalizado ────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px' }}>
      <p style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontSize: 13, fontWeight: 700, margin: 0 }}>
          {p.name}: USD {p.value?.toLocaleString('es-UY')}
        </p>
      ))}
    </div>
  );
};

// ─── Opciones de cruce de datos ───────────────────────────────────────────────
const CRUCES = [
  { id: 'autodata_vs_oferta',  label: 'Autodata vs Mejor oferta de revendedores',   desc: 'Compara el precio de toma Autodata con la mejor oferta recibida por vehículo' },
  { id: 'expectativa_vs_real', label: 'Expectativa del cliente vs Oferta real',     desc: 'Diferencia entre lo que espera el cliente y la mejor oferta del mercado' },
  { id: 'ml_vs_autodata',      label: 'MercadoLibre vs Autodata',                   desc: 'Promedio de publicaciones ML comparado con el precio de toma oficial' },
  { id: 'actividad_rev',       label: 'Actividad por revendedor',                   desc: 'Cantidad de ofertas y monto promedio por cada revendedor activo' },
  { id: 'dist_estados',        label: 'Distribución de estados de tasaciones',      desc: 'Cuántas publicaciones están completas, en proceso o sin ofertas' },
];

// ─── Marcas únicas ────────────────────────────────────────────────────────────
const MARCAS = ['Todas', ...Array.from(new Set(publicaciones.map(p => p.marca))).sort()];
const REVENDEDORES_UNICOS = ['Todos', ...Array.from(
  new Set(publicaciones.flatMap(p => p.revendedores.map(r => r.nombre)))
).sort()];

// ─── Select component ────────────────────────────────────────────────────────
function Select({ value, onChange, options, label }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#64748b' }}>{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full appearance-none px-3 py-2.5 pr-8 text-sm border rounded-lg outline-none focus:border-orange-400 transition bg-white"
          style={{ borderColor: '#e2e8f0', color: '#334155' }}
        >
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#94a3b8' }} />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DemoReportes() {
  // Filtros
  const [periodo,    setPeriodo]    = useState('Todo');
  const [marca,      setMarca]      = useState('Todas');
  const [revendedor, setRevendedor] = useState('Todos');
  const [estado,     setEstado]     = useState('Todos');
  const [precioMin,  setPrecioMin]  = useState('');
  const [precioMax,  setPrecioMax]  = useState('');

  // Cruce de datos
  const [crucesSel, setCrucesSel] = useState(new Set(['autodata_vs_oferta', 'expectativa_vs_real', 'actividad_rev']));

  // Resultado
  const [reporteGenerado, setReporteGenerado] = useState(false);
  const [generating,      setGenerating]      = useState(false);

  // Toggle cruce
  const toggleCruce = (id) => {
    setCrucesSel(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Limpiar filtros
  const limpiar = () => {
    setPeriodo('Todo'); setMarca('Todas'); setRevendedor('Todos');
    setEstado('Todos'); setPrecioMin(''); setPrecioMax('');
    setReporteGenerado(false);
  };

  // Datos filtrados
  const datosFiltrados = useMemo(() => {
    return publicaciones.filter(p => {
      if (marca !== 'Todas' && p.marca !== marca) return false;
      if (estado === 'Completa' && !(p.mercadoLibre && p.gerencia && p.revendedores.length > 0)) return false;
      if (estado === 'En proceso' && (p.mercadoLibre && p.gerencia && p.revendedores.length > 0)) return false;
      if (revendedor !== 'Todos' && !p.revendedores.some(r => r.nombre === revendedor)) return false;
      if (precioMin && p.autodata?.toma < Number(precioMin)) return false;
      if (precioMax && p.autodata?.toma > Number(precioMax)) return false;
      return true;
    });
  }, [marca, estado, revendedor, precioMin, precioMax]);

  // Datos para gráficas
  const chartAutodataVsOferta = useMemo(() =>
    datosFiltrados
      .filter(p => p.autodata && p.revendedores.length > 0)
      .slice(0, 8)
      .map(p => ({
        name: `${p.marca} ${p.modelo.slice(0, 6)}`,
        'Autodata Toma': p.autodata.toma,
        'Mejor Oferta':  Math.max(...p.revendedores.map(r => r.oferta)),
      }))
  , [datosFiltrados]);

  const chartExpectativaVsReal = useMemo(() =>
    datosFiltrados
      .filter(p => p.expectativaCliente && p.revendedores.length > 0)
      .slice(0, 8)
      .map(p => ({
        name: `${p.marca} ${p.modelo.slice(0, 6)}`,
        'Expectativa Cliente': p.expectativaCliente,
        'Mejor Oferta':        Math.max(...p.revendedores.map(r => r.oferta)),
      }))
  , [datosFiltrados]);

  const chartMLvsAutodata = useMemo(() =>
    datosFiltrados
      .filter(p => p.autodata && p.mercadoLibre)
      .slice(0, 8)
      .map(p => ({
        name: `${p.marca} ${p.modelo.slice(0, 6)}`,
        'ML Promedio':    p.mercadoLibre.promedio,
        'Autodata Toma':  p.autodata.toma,
      }))
  , [datosFiltrados]);

  const chartActividadRev = useMemo(() => {
    const stats = {};
    datosFiltrados.forEach(p => {
      p.revendedores.forEach(r => {
        if (!stats[r.nombre]) stats[r.nombre] = { nombre: r.nombre, ofertas: 0, total: 0 };
        stats[r.nombre].ofertas++;
        stats[r.nombre].total += r.oferta;
      });
    });
    return Object.values(stats)
      .sort((a, b) => b.ofertas - a.ofertas)
      .slice(0, 8)
      .map(r => ({ name: r.nombre, Ofertas: r.ofertas, 'Promedio USD': Math.round(r.total / r.ofertas) }));
  }, [datosFiltrados]);

  const chartDistEstados = useMemo(() => {
    const completas  = datosFiltrados.filter(p => p.mercadoLibre && p.gerencia && p.revendedores.length > 0).length;
    const enProceso  = datosFiltrados.filter(p => !(p.mercadoLibre && p.gerencia) && p.revendedores.length > 0).length;
    const sinOfertas = datosFiltrados.filter(p => p.revendedores.length === 0).length;
    return [
      { name: 'Completas',   value: completas,  fill: '#22c55e' },
      { name: 'En proceso',  value: enProceso,  fill: '#f97316' },
      { name: 'Sin ofertas', value: sinOfertas, fill: '#94a3b8' },
    ];
  }, [datosFiltrados]);

  const totalOfertas = datosFiltrados.reduce((s, p) => s + p.revendedores.length, 0);
  const mejorOferta  = datosFiltrados.length > 0 && datosFiltrados.some(p => p.revendedores.length > 0)
    ? Math.max(...datosFiltrados.flatMap(p => p.revendedores.map(r => r.oferta)))
    : 0;

  const handleGenerar = () => {
    setGenerating(true);
    setTimeout(() => { setGenerating(false); setReporteGenerado(true); }, 900);
  };

  const hasFiltrosActivos = marca !== 'Todas' || revendedor !== 'Todos' || estado !== 'Todos' || precioMin || precioMax;

  return (
    <div className="min-h-screen" style={{ background: '#f1f5f9' }}>
      <DemoNav active="reportes" />

      <main className="px-4 sm:px-6 py-6 max-w-7xl mx-auto">

        {/* Page header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>Generador de Reportes</h2>
            <p className="text-sm mt-0.5" style={{ color: '#64748b' }}>
              Aplicá filtros, seleccioná los cruces de datos y generá el reporte personalizado
            </p>
          </div>
          {hasFiltrosActivos && (
            <button onClick={limpiar} className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition hover:bg-gray-200" style={{ color: '#64748b' }}>
              <RefreshCw size={13} /> Limpiar filtros
            </button>
          )}
        </div>

        {/* ── Panel 1: Filtros ─────────────────────────────────────────────── */}
        <div className="demo-card p-5 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={16} style={{ color: '#f97316' }} />
            <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#0f172a' }}>Filtros</h3>
            {hasFiltrosActivos && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full" style={{ background: '#fff7ed', color: '#ea580c' }}>
                Activos
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <Select label="Período"     value={periodo}    onChange={setPeriodo}
              options={['Todo', 'Esta semana', 'Este mes', 'Últimos 3 meses', 'Este año']} />
            <Select label="Marca"       value={marca}      onChange={setMarca}      options={MARCAS} />
            <Select label="Revendedor"  value={revendedor} onChange={setRevendedor} options={REVENDEDORES_UNICOS} />
            <Select label="Estado"      value={estado}     onChange={setEstado}
              options={['Todos', 'Completa', 'En proceso']} />
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#64748b' }}>
                Precio (USD)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={precioMin}
                  onChange={e => setPrecioMin(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border rounded-lg outline-none focus:border-orange-400 transition"
                  style={{ borderColor: '#e2e8f0', color: '#334155' }}
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={precioMax}
                  onChange={e => setPrecioMax(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border rounded-lg outline-none focus:border-orange-400 transition"
                  style={{ borderColor: '#e2e8f0', color: '#334155' }}
                />
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t" style={{ borderColor: '#f1f5f9' }}>
            <span className="text-xs" style={{ color: '#94a3b8' }}>
              {datosFiltrados.length} publicaciones coinciden con los filtros seleccionados
            </span>
          </div>
        </div>

        {/* ── Panel 2: Cruce de datos ──────────────────────────────────────── */}
        <div className="demo-card p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 size={16} style={{ color: '#f97316' }} />
            <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#0f172a' }}>Cruce de datos</h3>
            <span className="text-xs ml-auto" style={{ color: '#94a3b8' }}>
              {crucesSel.size} seleccionados
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CRUCES.map(c => {
              const sel = crucesSel.has(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggleCruce(c.id)}
                  className="flex items-start gap-3 p-4 rounded-xl border-2 text-left transition"
                  style={{
                    background:   sel ? '#fff7ed' : 'white',
                    borderColor:  sel ? '#f97316' : '#e2e8f0',
                  }}
                >
                  <div className="mt-0.5 flex-shrink-0" style={{ color: sel ? '#f97316' : '#cbd5e1' }}>
                    {sel ? <CheckSquare size={18} /> : <Square size={18} />}
                  </div>
                  <div>
                    <div className="text-sm font-semibold mb-0.5" style={{ color: '#0f172a' }}>{c.label}</div>
                    <div className="text-xs leading-relaxed" style={{ color: '#64748b' }}>{c.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Botón Generar ────────────────────────────────────────────────── */}
        <div className="flex justify-center mb-8">
          <button
            onClick={handleGenerar}
            disabled={generating || crucesSel.size === 0 || datosFiltrados.length === 0}
            className="flex items-center gap-2 px-8 py-3.5 text-white font-bold text-base rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', boxShadow: '0 4px 20px rgba(249,115,22,0.35)' }}
          >
            {generating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <FileText size={18} />
                Generar Reporte
              </>
            )}
          </button>
        </div>

        {/* ── REPORTE GENERADO ─────────────────────────────────────────────── */}
        {reporteGenerado && (
          <div>
            {/* Header del reporte */}
            <div className="demo-card p-5 mb-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#22c55e' }}>Reporte generado</span>
                  </div>
                  <h3 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>
                    Análisis de {datosFiltrados.length} publicaciones
                    {marca !== 'Todas' ? ` · ${marca}` : ''}
                    {estado !== 'Todos' ? ` · ${estado}` : ''}
                  </h3>
                  <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>
                    {crucesSel.size} cruces de datos incluidos
                  </p>
                </div>
                <div className="flex gap-2">
                  <button className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border rounded-lg transition hover:bg-gray-50"
                    style={{ borderColor: '#e2e8f0', color: '#475569' }}>
                    <Download size={14} /> Excel
                  </button>
                  <button className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border rounded-lg transition hover:bg-gray-50"
                    style={{ borderColor: '#e2e8f0', color: '#475569' }}>
                    <Download size={14} /> PDF
                  </button>
                </div>
              </div>
            </div>

            {/* KPI resumen */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              {[
                { label: 'Publicaciones',   value: datosFiltrados.length,       color: '#3b82f6' },
                { label: 'Ofertas totales', value: totalOfertas,                color: '#f97316' },
                { label: 'Mejor oferta',    value: mejorOferta > 0 ? `$${(mejorOferta/1000).toFixed(1)}k` : '—', color: '#22c55e' },
                { label: 'Prom. ofertas/pub', value: datosFiltrados.length > 0 ? (totalOfertas / datosFiltrados.length).toFixed(1) : '0', color: '#8b5cf6' },
              ].map((k, i) => (
                <div key={i} className="demo-card p-4 text-center">
                  <div className="text-2xl font-bold mb-0.5" style={{ fontFamily: 'Outfit, sans-serif', color: k.color }}>{k.value}</div>
                  <div className="text-xs" style={{ color: '#64748b' }}>{k.label}</div>
                </div>
              ))}
            </div>

            {/* Gráficas según cruces seleccionados */}
            <div className="space-y-4">

              {/* Autodata vs Mejor Oferta */}
              {crucesSel.has('autodata_vs_oferta') && chartAutodataVsOferta.length > 0 && (
                <div className="demo-card p-5">
                  <h4 className="text-sm font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>
                    Autodata vs Mejor Oferta de Revendedores
                  </h4>
                  <p className="text-xs mb-4" style={{ color: '#94a3b8' }}>
                    Precio de toma oficial (Autodata) comparado con la oferta más alta recibida por vehículo
                  </p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartAutodataVsOferta} margin={{ top: 0, right: 0, left: -10, bottom: 0 }} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(249,115,22,0.05)' }} />
                      <Legend wrapperStyle={{ fontSize: 12, color: '#64748b' }} />
                      <Bar dataKey="Autodata Toma" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Mejor Oferta"  fill="#f97316" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Expectativa vs Real */}
              {crucesSel.has('expectativa_vs_real') && chartExpectativaVsReal.length > 0 && (
                <div className="demo-card p-5">
                  <h4 className="text-sm font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>
                    Expectativa del Cliente vs Oferta Real
                  </h4>
                  <p className="text-xs mb-4" style={{ color: '#94a3b8' }}>
                    Diferencia entre el precio que espera el cliente y la mejor oferta del mercado
                  </p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartExpectativaVsReal} margin={{ top: 0, right: 0, left: -10, bottom: 0 }} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59,130,246,0.05)' }} />
                      <Legend wrapperStyle={{ fontSize: 12, color: '#64748b' }} />
                      <Bar dataKey="Expectativa Cliente" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Mejor Oferta"        fill="#22c55e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* ML vs Autodata */}
              {crucesSel.has('ml_vs_autodata') && chartMLvsAutodata.length > 0 && (
                <div className="demo-card p-5">
                  <h4 className="text-sm font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>
                    MercadoLibre vs Autodata
                  </h4>
                  <p className="text-xs mb-4" style={{ color: '#94a3b8' }}>
                    Promedio de ML comparado con el precio de toma oficial de Autodata
                  </p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartMLvsAutodata} margin={{ top: 0, right: 0, left: -10, bottom: 0 }} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(139,92,246,0.05)' }} />
                      <Legend wrapperStyle={{ fontSize: 12, color: '#64748b' }} />
                      <Bar dataKey="ML Promedio"   fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Autodata Toma" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Actividad por revendedor */}
              {crucesSel.has('actividad_rev') && chartActividadRev.length > 0 && (
                <div className="demo-card p-5">
                  <h4 className="text-sm font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>
                    Actividad por Revendedor
                  </h4>
                  <p className="text-xs mb-4" style={{ color: '#94a3b8' }}>
                    Cantidad de ofertas realizadas en las publicaciones seleccionadas
                  </p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartActividadRev} layout="vertical" margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis dataKey="name" type="category" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false} width={90} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(249,115,22,0.05)' }} />
                      <Bar dataKey="Ofertas" fill="#f97316" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Distribución de estados */}
              {crucesSel.has('dist_estados') && (
                <div className="demo-card p-5">
                  <h4 className="text-sm font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>
                    Distribución de Estados
                  </h4>
                  <p className="text-xs mb-4" style={{ color: '#94a3b8' }}>
                    Cuántas publicaciones están en cada estado dentro del filtro seleccionado
                  </p>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={chartDistEstados} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" name="Publicaciones" radius={[4, 4, 0, 0]}>
                        {chartDistEstados.map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Tabla de datos filtrados */}
            <div className="demo-card overflow-hidden mt-4">
              <div className="px-5 py-4" style={{ borderBottom: '1px solid #e2e8f0' }}>
                <h4 className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>
                  Detalle — {datosFiltrados.length} publicaciones
                </h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <tr>
                      {['Vehículo', 'Patente', 'Autodata Toma', 'ML Promedio', 'Mejor Oferta', 'Ofertas', 'Estado'].map((h, i) => (
                        <th key={i} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748b' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody style={{ background: 'white' }}>
                    {datosFiltrados.map((p, idx) => {
                      const mejor = p.revendedores.length > 0 ? Math.max(...p.revendedores.map(r => r.oferta)) : null;
                      const completa = p.mercadoLibre && p.gerencia && p.revendedores.length > 0;
                      return (
                        <tr key={p.id} style={{ borderBottom: idx < datosFiltrados.length - 1 ? '1px solid #f8fafc' : 'none' }} className="hover:bg-gray-50 transition">
                          <td className="px-5 py-3.5 whitespace-nowrap">
                            <div className="text-sm font-semibold" style={{ color: '#0f172a' }}>{p.marca} {p.modelo}</div>
                            <div className="text-xs" style={{ color: '#94a3b8' }}>{p.año}</div>
                          </td>
                          <td className="px-5 py-3.5 text-sm font-mono whitespace-nowrap" style={{ color: '#475569' }}>{p.patente}</td>
                          <td className="px-5 py-3.5 text-sm whitespace-nowrap" style={{ color: '#475569' }}>
                            {p.autodata ? `$${p.autodata.toma?.toLocaleString('es-UY')}` : '—'}
                          </td>
                          <td className="px-5 py-3.5 text-sm whitespace-nowrap" style={{ color: '#475569' }}>
                            {p.mercadoLibre ? `$${p.mercadoLibre.promedio?.toLocaleString('es-UY')}` : '—'}
                          </td>
                          <td className="px-5 py-3.5 text-sm font-bold whitespace-nowrap" style={{ color: mejor ? '#22c55e' : '#94a3b8' }}>
                            {mejor ? `$${mejor.toLocaleString('es-UY')}` : '—'}
                          </td>
                          <td className="px-5 py-3.5 text-sm font-bold whitespace-nowrap" style={{ color: p.revendedores.length > 5 ? '#f97316' : '#0f172a' }}>
                            {p.revendedores.length}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              completa ? 'demo-badge-success' : 'demo-badge-warning'
                            }`}>
                              {completa ? 'Completa' : 'En proceso'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}

        {/* Estado vacío */}
        {!reporteGenerado && (
          <div className="demo-card p-10 text-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#fff7ed' }}>
              <FileText size={24} style={{ color: '#f97316' }} />
            </div>
            <h3 className="text-base font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>
              Configurá y generá tu reporte
            </h3>
            <p className="text-sm max-w-md mx-auto" style={{ color: '#64748b' }}>
              Seleccioná los filtros, elegí los cruces de datos que necesitás y presioná "Generar Reporte" para ver el análisis.
            </p>
          </div>
        )}

      </main>
    </div>
  );
}
